// The tide curve on a harbour card.
//
// The API gives four numbers a day — the turns — and a reader wants the shape
// between them: is the water halfway up, is it nearly out, how long have I
// got. That shape is not in the response and has to be drawn.
//
// SVG and no dependency, like the river sparkline beside it. A chart library
// for one curve would cost more than the whole map bundle saves.

import { escapeHtml } from './escape.ts';

const WIDTH = 320;
const HEIGHT = 136;
const PAD_L = 26;
const PAD_R = 8;
const PAD_T = 26;
const PAD_B = 34;

export interface TideDay {
  station: string;
  harbour: string;
  events: { type: string; at: number; height: number | null }[];
  sunrise: number | null;
  sunset: number | null;
}

const isHigh = (type: string) => /high/i.test(type);

const clock = (ms: number) =>
  new Date(ms).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

/**
 * Height at a moment, interpolated between the turns either side.
 *
 * A cosine rather than a straight line, because that is close to what the sea
 * actually does: slow at the turns, fastest halfway between. A straight line
 * would put the water level visibly wrong for most of every cycle, and the
 * shape is the entire reason for drawing this rather than listing four times.
 *
 * (The mariner's rule of twelfths is the same curve, sampled coarsely.)
 */
function heightAt(
  at: number,
  events: { at: number; height: number | null }[],
): number | null {
  if (events.length < 2) return null;

  for (let i = 0; i < events.length - 1; i += 1) {
    const a = events[i]!;
    const b = events[i + 1]!;
    if (at < a.at || at > b.at) continue;
    if (a.height === null || b.height === null) return null;

    const phase = (at - a.at) / (b.at - a.at);
    // cos runs 1 → -1 over the half cycle, so this eases out of one turn and
    // into the next.
    return a.height + (b.height - a.height) * (1 - Math.cos(phase * Math.PI)) / 2;
  }
  return null;
}

/**
 * The curve, the turns, the daylight, and now.
 *
 * Returns '' rather than an empty frame when there is nothing to draw: a chart
 * with no data is furniture that implies a fault.
 */
export function renderTideChart(day: TideDay, now = Date.now()): string {
  const events = [...day.events].sort((a, b) => a.at - b.at);
  const priced = events.filter((e) => e.height !== null);
  if (priced.length < 2) return '';

  const from = now - 3 * 3_600_000;
  const to = now + 21 * 3_600_000;

  const heights = priced.map((e) => e.height!);
  // Padded, so the peaks are not drawn touching the frame.
  const lo = Math.min(...heights) - 0.3;
  const hi = Math.max(...heights) + 0.3;

  const x = (t: number) => PAD_L + ((t - from) / (to - from)) * (WIDTH - PAD_L - PAD_R);
  const y = (h: number) => PAD_T + (1 - (h - lo) / (hi - lo)) * (HEIGHT - PAD_T - PAD_B);

  // Sampled every ten minutes: smooth at this width, and cheap.
  const step = 10 * 60_000;
  const points: string[] = [];
  for (let t = from; t <= to; t += step) {
    const h = heightAt(t, events);
    if (h === null) continue;
    points.push(`${x(t).toFixed(1)},${y(h).toFixed(1)}`);
  }
  if (points.length < 2) return '';

  const curve = `M${points.join(' L')}`;
  const floor = HEIGHT - PAD_B;
  const fill = `${curve} L${x(to).toFixed(1)},${floor} L${x(from).toFixed(1)},${floor} Z`;

  /*
   * Night, shaded — rather than daylight.
   *
   * It answers the question people pair with a tide time: will it be light
   * when I get there. Drawn as darkness at the edges instead of a bright band
   * in the middle because the middle already has the curve in it, and a yellow
   * wash over a blue fill comes out olive. Shading the absence keeps the water
   * the colour of water.
   */
  const night = (a: number, b: number) => {
    const x1 = Math.max(x(a), PAD_L);
    const x2 = Math.min(x(b), WIDTH - PAD_R);
    if (x2 - x1 < 1) return '';
    return `<rect class="tc-night" x="${x1.toFixed(1)}" y="${PAD_T}"
      width="${(x2 - x1).toFixed(1)}" height="${(HEIGHT - PAD_T - PAD_B).toFixed(1)}" />`;
  };
  const band = day.sunrise && day.sunset
    ? night(from, day.sunrise) + night(day.sunset, to)
    : '';

  const marks = priced
    .filter((e) => e.at >= from && e.at <= to)
    .map((e) => {
      const cx = x(e.at);
      const cy = y(e.height!);
      // Labels flip to the inside near the edges, or they run off the frame.
      const anchor = cx < PAD_L + 30 ? 'start' : cx > WIDTH - PAD_R - 30 ? 'end' : 'middle';
      const above = isHigh(e.type);
      return `
        <circle class="${above ? 'tc-high' : 'tc-low'}" cx="${cx.toFixed(1)}"
                cy="${cy.toFixed(1)}" r="3.5" />
        <text class="tc-mark" x="${cx.toFixed(1)}" y="${(cy + (above ? -8 : 13)).toFixed(1)}"
              text-anchor="${anchor}">${clock(e.at)}</text>`;
    })
    .join('');

  const nowX = x(now);
  const nowLine = `
    <line class="tc-now" x1="${nowX.toFixed(1)}" y1="${PAD_T}"
          x2="${nowX.toFixed(1)}" y2="${floor}" />`;

  // Two gridlines and their labels: enough to read a height off, few enough
  // not to compete with the curve.
  const axis = [lo + 0.3, hi - 0.3].map((h) => `
    <text class="tc-axis" x="${PAD_L - 5}" y="${(y(h) + 3).toFixed(1)}"
          text-anchor="end">${h.toFixed(1)}</text>`).join('');

  const hours = [];
  for (let t = from; t <= to; t += 6 * 3_600_000) {
    const hour = new Date(t).getHours();
    hours.push(`<text class="tc-axis" x="${x(t).toFixed(1)}" y="${HEIGHT - 8}"
        text-anchor="middle">${String(hour).padStart(2, '0')}</text>`);
  }

  return `
    <figure class="tide-chart">
      <svg viewBox="0 0 ${WIDTH} ${HEIGHT}" width="100%" role="img"
           aria-label="Tide curve for ${escapeHtml(day.harbour)} over the next day">
        ${band}
        <path class="tc-fill" d="${fill}" />
        <path class="tc-line" d="${curve}" />
        ${nowLine}
        ${marks}
        ${axis}
        ${hours.join('')}
        <text class="tc-axis tc-unit" x="2" y="${PAD_T - 8}">m</text>
      </svg>
    </figure>`;
}
